// Şub 27, 2026 — DPD Aylık Fatura Özeti
// dpd_invoice_daily'den tur bazlı aylık agregasyon
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Receipt, RefreshCw, Loader2, TrendingUp, Package, DollarSign, Award, MinusCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n, d = 2) => (typeof n === 'number' ? n.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d }) : '-');
const fmt0 = (n) => (typeof n === 'number' ? Math.round(n).toLocaleString('tr-TR') : '-');
const eur = (n) => (typeof n === 'number' ? `€${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-');

export default function DPDMonthlyInvoice() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [months, setMonths] = useState([]);
  const [ym, setYm] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!firmId) return;
    api.get(`/dpd/invoice-months?firm_id=${firmId}`).then((r) => {
      const ms = r.data?.data || [];
      setMonths(ms);
      if (ms.length > 0 && !ym) setYm(ms[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId]);

  const fetchData = useCallback(async () => {
    if (!firmId || !ym) return;
    setLoading(true);
    try {
      const r = await api.get(`/dpd/invoice-monthly?firm_id=${firmId}&year_month=${ym}`);
      setData(r.data?.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fatura verisi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId, ym]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const t2 = data?.totals || {};
  const rows = data?.tour_rows || [];

  return (
    <div className="space-y-4" data-testid="dpd-monthly-invoice-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-600" />
            {t('dpd.monthlyInvoice.title')}
          </CardTitle>
          <CardDescription>
            {t('dpd.monthlyInvoice.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="invoice-firm-select"><SelectValue placeholder="Firma" /></SelectTrigger>
              <SelectContent>
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={ym} onValueChange={setYm}>
              <SelectTrigger data-testid="invoice-month-select"><SelectValue placeholder="Ay" /></SelectTrigger>
              <SelectContent>
                {months.length === 0 && <div className="p-2 text-xs text-gray-500">Fatura yok</div>}
                {months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={fetchData} disabled={loading} variant="outline" data-testid="invoice-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Yenile
            </Button>
          </div>

          {/* Grand totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KpiCard icon={Package} label={t('dpd.monthlyInvoice.kpi.totalParcels')} value={fmt0(t2['Geleverde pakketten'])}
                     subtitle={t('dpd.monthlyInvoice.kpi.daysAndTours', { days: data?.days || 0, tours: t2.tour_count || 0 })} color="indigo" />
            <KpiCard icon={TrendingUp} label={t('dpd.monthlyInvoice.kpi.reliability')} value={t2.avg_reliability ? `%${fmt(t2.avg_reliability)}` : '-'}
                     subtitle="Leveringsbetrouwbaarheid" color="emerald" />
            <KpiCard icon={DollarSign} label={t('dpd.monthlyInvoice.kpi.basePay')} value={eur(t2.Basisvergoeding)}
                     subtitle={t('dpd.monthlyInvoice.kpi.bonusMalus', { bonus: eur(t2.performance_bonus), malus: eur(t2.performance_malus) })} color="blue" />
            <KpiCard icon={Award} label={t('dpd.monthlyInvoice.kpi.netPay')} value={eur(t2.net_pay)}
                     subtitle={t('dpd.monthlyInvoice.kpi.netSubtitle')} color="amber" />
          </div>

          {/* Tour breakdown */}
          <div className="mb-2">
            <h3 className="text-sm font-semibold mb-2">Tur Bazlı Aylık Dağılım</h3>
            <div className="rounded-lg border overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">Tur</TableHead>
                    <TableHead className="text-right w-[70px]">Aktif Gün</TableHead>
                    <TableHead className="text-right w-[100px]">Teslim Paket</TableHead>
                    <TableHead className="text-right w-[80px]">Stops</TableHead>
                    <TableHead className="text-right w-[80px]">Pickup</TableHead>
                    <TableHead className="text-right w-[80px]">Km</TableHead>
                    <TableHead className="text-right w-[80px]">Güv. %</TableHead>
                    <TableHead className="text-right w-[110px]">Base (€)</TableHead>
                    <TableHead className="text-right w-[90px]">
                      <span className="inline-flex items-center gap-1"><Award className="h-3 w-3 text-emerald-600" />Bonus</span>
                    </TableHead>
                    <TableHead className="text-right w-[90px]">
                      <span className="inline-flex items-center gap-1"><MinusCircle className="h-3 w-3 text-red-600" />Malus</span>
                    </TableHead>
                    <TableHead className="text-right w-[110px]">Net (€)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: 11 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-3 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {!loading && rows.length === 0 && (
                    <TableRow><TableCell colSpan={11} className="text-center text-gray-500 py-8">
                      Bu ay için fatura verisi yok. Subco Details Excel'leri e-postadan gelmiş olmalı.
                    </TableCell></TableRow>
                  )}
                  {!loading && rows.map((r) => (
                    <TableRow key={r.tour} data-testid={`inv-row-${r.tour}`}>
                      <TableCell><Badge variant="outline" className="font-mono">{r.tour}</Badge></TableCell>
                      <TableCell className="text-right">{r.days_active}</TableCell>
                      <TableCell className="text-right font-medium">{fmt0(r['Geleverde pakketten'])}</TableCell>
                      <TableCell className="text-right">{fmt0(r.delivered_stops_total)}</TableCell>
                      <TableCell className="text-right">{fmt0(r.Afhaalpakketten)}</TableCell>
                      <TableCell className="text-right">{fmt(r.km, 1)}</TableCell>
                      <TableCell className="text-right">%{fmt(r.avg_reliability)}</TableCell>
                      <TableCell className="text-right">{eur(r.Basisvergoeding)}</TableCell>
                      <TableCell className="text-right text-emerald-700">{eur(r.performance_bonus)}</TableCell>
                      <TableCell className="text-right text-red-700">{eur(r.performance_malus)}</TableCell>
                      <TableCell className="text-right font-semibold">{eur(r.net_pay)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {rows.length > 0 && (
                  <TableFooter>
                    <TableRow className="bg-gray-100 dark:bg-gray-800">
                      <TableCell className="font-bold">Toplam</TableCell>
                      <TableCell className="text-right">{data?.days || 0}</TableCell>
                      <TableCell className="text-right font-bold">{fmt0(t2['Geleverde pakketten'])}</TableCell>
                      <TableCell className="text-right">{fmt0(t2.delivered_stops_total)}</TableCell>
                      <TableCell className="text-right">{fmt0(t2.Afhaalpakketten)}</TableCell>
                      <TableCell className="text-right">{fmt(t2.km, 1)}</TableCell>
                      <TableCell className="text-right">%{fmt(t2.avg_reliability)}</TableCell>
                      <TableCell className="text-right">{eur(t2.Basisvergoeding)}</TableCell>
                      <TableCell className="text-right text-emerald-700">{eur(t2.performance_bonus)}</TableCell>
                      <TableCell className="text-right text-red-700">{eur(t2.performance_malus)}</TableCell>
                      <TableCell className="text-right font-bold">{eur(t2.net_pay)}</TableCell>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </div>
          </div>

          {/* Product code breakdown */}
          {rows.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold mb-2">Ürün Kodu Bazlı Dağılım (Aylık Toplam)</h3>
              <div className="rounded-lg border overflow-x-auto">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow>
                      {['03D', '03XD', '03SD', '03XSD', '03COD', '03SWAP', '04DAD', '04DAV', '04AM1D', '04AM2D', '05MAST'].map((k) => (
                        <TableHead key={k} className="text-right text-[10px]">{k}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      {['03D', '03XD', '03SD', '03XSD', '03COD', '03SWAP', '04DAD', '04DAV', '04AM1D', '04AM2D', '05MAST'].map((k) => (
                        <TableCell key={k} className="text-right font-medium">{fmt0(t[k])}</TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Kodlar: 03D=Standart teslimat, 03XD=Extra, 03SD=Saturday, 04DAD/04DAV=Weekend, 05MAST=Master, vs.
              </p>
            </div>
          )}

          {data?.source_files?.length > 0 && (
            <div className="mt-4 text-[11px] text-gray-500">
              Kaynak: {data.source_files.slice(0, 3).join(', ')}
              {data.source_files.length > 3 && ` (+${data.source_files.length - 3} dosya)`}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subtitle, color }) {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${colors[color]}`}>
      <div className="flex items-center gap-2 text-xs opacity-80"><Icon className="h-4 w-4" />{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      <div className="text-[11px] opacity-70 mt-0.5">{subtitle}</div>
    </div>
  );
}
